import _startCase from 'lodash/startCase';
import type { SemanticCOLORS } from 'semantic-ui-react';

import type { VulnerabilitySeverity } from '@models';

export type { VulnerabilitySeverity };

/**
 * Qualitative CVSS rating scale, as defined by the CVSS specification
 * itself. Deliberately distinct from `VulnerabilitySeverity` (this
 * platform's own, Grype-matching vocabulary): the CVSS scale has no
 * `negligible` tier and labels its bottom tier `none`. `unknown` is a local
 * sentinel for "no rating and no score to band" - it has no CVSS meaning.
 */
export type CvssRating =
  'critical' | 'high' | 'medium' | 'low' | 'none' | 'unknown';

export const COLOR_BY_SEVERITY = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'violet',
  negligible: undefined,
  unknown: 'grey',
} as const satisfies Record<
  Lowercase<VulnerabilitySeverity>,
  SemanticCOLORS | undefined
>;

/**
 * Severity levels ordered from most to least severe.
 */
export const SEVERITY_ORDER: Lowercase<VulnerabilitySeverity>[] = [
  'critical',
  'high',
  'medium',
  'low',
  'negligible',
  'unknown',
];

/**
 * Severity options for an `EnumList` filter, ordered most to least severe with
 * title-cased labels, so severity filters stay identical wherever they appear.
 */
export const SEVERITY_FILTER_OPTIONS = SEVERITY_ORDER.map(value => ({
  label: _startCase(value),
  value,
}));

/**
 * Human-readable score ranges for each CVSS rating.
 */
export const CVSS_RANGES: Partial<Record<CvssRating, string>> = {
  critical: 'Scores 9.0 - 10.0',
  high: 'Scores 7.0 - 8.9',
  medium: 'Scores 4.0 - 6.9',
  low: 'Scores 0.1 - 3.9',
  none: 'Scores 0.0',
};
